import React, { useState, useEffect } from "react";
import { Box, Button, Typography, useTheme, Chip, CircularProgress } from "@mui/material";
import { tokens } from "../../theme";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import VerifiedUserOutlinedIcon from "@mui/icons-material/VerifiedUserOutlined";
import Header from "../../components/Header";
import StatBox from "../../components/StatBox";
import { API_BASE_URL } from "../../config";
import { Link } from "react-router-dom";

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const authToken = sessionStorage.getItem("authToken");
      const res = await fetch(`${API_BASE_URL}/admin/stats`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ authToken }),
      });
      const data = await res.json();
      if (data.success && data.stats) {
        setStats(data.stats);
      }
    } catch (err) {
      console.error("Erreur stats dashboard:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 30000); // Rafraîchissement automatique toutes les 30s
    return () => clearInterval(interval);
  }, []);

  const getRideStateChip = (state) => {
    switch (state) {
      case 0:
        return <Chip label="En attente" size="small" sx={{ backgroundColor: colors.blueAccent[600], color: "#fff" }} />;
      case 1:
        return <Chip label="En cours" size="small" sx={{ backgroundColor: colors.greenAccent[600], color: "#fff" }} />;
      case 2:
        return <Chip label="Terminée" size="small" sx={{ backgroundColor: colors.greenAccent[500], color: "#fff" }} />;
      case 3:
        return <Chip label="Annulée" size="small" sx={{ backgroundColor: "#d32f2f", color: "#fff" }} />;
      default:
        return <Chip label="Inconnu" size="small" />;
    }
  };

  return (
    <Box m="20px">
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap="10px">
        <Header title="TABLEAU DE BORD" subtitle="Vue d'ensemble en temps réel de la plateforme LinkerVTC" />

        <Box display="flex" gap="10px">
          <Button
            onClick={fetchStats}
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
          >
            Rafraîchir
          </Button>
          <Button
            component={Link}
            to="/drivers-map"
            sx={{
              backgroundColor: colors.greenAccent[600],
              color: colors.grey[100],
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
          >
            Carte Chauffeurs
          </Button>
        </Box>
      </Box>

      {/* GRID & STATS */}
      {loading && !stats ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="40vh">
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <Box
          display="grid"
          gridTemplateColumns="repeat(12, 1fr)"
          gridAutoRows="140px"
          gap="20px"
        >
          {/* ROW 1 : STAT CARDS */}
          <Box
            gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
            backgroundColor={colors.primary[400]}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="4px"
          >
            <StatBox
              title={stats?.total_users?.toLocaleString() || "0"}
              subtitle="Passagers Inscrits"
              progress={stats?.total_users ? "1" : "0"}
              increase={`+${stats?.new_users_today || 0} aujourd'hui`}
              icon={<PeopleAltOutlinedIcon sx={{ color: colors.greenAccent[600], fontSize: "26px" }} />}
            />
          </Box>

          <Box
            gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
            backgroundColor={colors.primary[400]}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="4px"
          >
            <StatBox
              title={stats?.total_drivers?.toLocaleString() || "0"}
              subtitle={`Chauffeurs (${stats?.online_drivers || 0} en ligne)`}
              progress={stats?.total_drivers ? (stats.verified_drivers / stats.total_drivers).toFixed(2) : "0"}
              increase={`${stats?.verified_drivers || 0} validés`}
              icon={<DirectionsCarOutlinedIcon sx={{ color: colors.greenAccent[600], fontSize: "26px" }} />}
            />
          </Box>

          <Box
            gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
            backgroundColor={colors.primary[400]}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="4px"
          >
            <StatBox
              title={stats?.total_rides?.toLocaleString() || "0"}
              subtitle="Courses Totales"
              progress={stats?.total_rides ? (stats.completed_rides / stats.total_rides).toFixed(2) : "0"}
              increase={`${stats?.completed_rides || 0} terminées`}
              icon={<RouteOutlinedIcon sx={{ color: colors.greenAccent[600], fontSize: "26px" }} />}
            />
          </Box>

          <Box
            gridColumn={{ xs: "span 12", sm: "span 6", md: "span 3" }}
            backgroundColor={colors.primary[400]}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius="4px"
          >
            <StatBox
              title={`${(stats?.total_revenue || 0).toLocaleString()} DZD`}
              subtitle="Revenus Abonnements"
              progress="1"
              increase={`${(stats?.revenue_this_month || 0).toLocaleString()} DZD ce mois`}
              icon={<AttachMoneyOutlinedIcon sx={{ color: colors.greenAccent[600], fontSize: "26px" }} />}
            />
          </Box>

          {/* ROW 2 : RECENT RIDES & ACTIONS */}
          <Box
            gridColumn={{ xs: "span 12", md: "span 8" }}
            gridRow="span 3"
            backgroundColor={colors.primary[400]}
            p="20px"
            borderRadius="4px"
            overflow="auto"
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb="15px">
              <Typography variant="h5" fontWeight="600" color={colors.grey[100]}>
                Courses Récentes en Direct
              </Typography>
              <Button component={Link} to="/rides" sx={{ color: colors.greenAccent[500] }}>
                Voir toutes les courses →
              </Button>
            </Box>

            {(!stats?.recent_rides || stats.recent_rides.length === 0) ? (
              <Box p="30px" textAlign="center" color={colors.grey[300]}>
                Aucune course enregistrée pour le moment.
              </Box>
            ) : (
              stats.recent_rides.map((ride) => (
                <Box
                  key={ride.id}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  borderBottom={`1px solid ${colors.primary[500]}`}
                  p="12px 0"
                >
                  <Box>
                    <Typography color={colors.greenAccent[500]} fontWeight="600">
                      {ride.passenger_name} ➔ {ride.driver_name}
                    </Typography>
                    <Typography variant="body2" color={colors.grey[300]}>
                      {ride.distance} km • {ride.duration} min • {new Date(ride.created_at).toLocaleDateString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                    </Typography>
                  </Box>

                  <Box display="flex" alignItems="center" gap="15px">
                    <Typography fontWeight="bold" color={colors.grey[100]}>
                      {ride.price} DZD
                    </Typography>
                    {getRideStateChip(ride.state)}
                  </Box>
                </Box>
              ))
            )}
          </Box>

          {/* ROW 2 RIGHT : ACTIONS & STATUS */}
          <Box
            gridColumn={{ xs: "span 12", md: "span 4" }}
            gridRow="span 3"
            backgroundColor={colors.primary[400]}
            p="20px"
            borderRadius="4px"
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
          >
            <Box>
              <Typography variant="h5" fontWeight="600" color={colors.grey[100]} mb="15px">
                Vérifications en Attente
              </Typography>

              <Box p="15px" backgroundColor={colors.primary[500]} borderRadius="4px" mb="15px">
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="h4" color={colors.greenAccent[500]} fontWeight="bold">
                    {stats?.pending_drivers || 0}
                  </Typography>
                  <VerifiedUserOutlinedIcon sx={{ color: colors.greenAccent[500], fontSize: "32px" }} />
                </Box>
                <Typography variant="body2" color={colors.grey[200]} mt="5px">
                  Dossiers chauffeurs en attente de vérification
                </Typography>
                <Button
                  component={Link}
                  to="/verification"
                  variant="contained"
                  fullWidth
                  sx={{ mt: "10px", backgroundColor: colors.blueAccent[700] }}
                >
                  Examiner les dossiers
                </Button>
              </Box>

              <Typography variant="h5" fontWeight="600" color={colors.grey[100]} mb="10px">
                Raccourcis Administrateur
              </Typography>
              <Box display="flex" flexDirection="column" gap="8px">
                <Button component={Link} to="/users" variant="outlined" color="info" fullWidth sx={{ justifyContent: "flex-start" }}>
                  👥 Gérer les Passagers
                </Button>
                <Button component={Link} to="/drivers" variant="outlined" color="success" fullWidth sx={{ justifyContent: "flex-start" }}>
                  🚗 Gérer les Chauffeurs
                </Button>
                <Button component={Link} to="/payments" variant="outlined" color="secondary" fullWidth sx={{ justifyContent: "flex-start" }}>
                  💳 Voir les Abonnements & Paiements
                </Button>
                <Button component={Link} to="/settings" variant="outlined" sx={{ color: colors.grey[100], borderColor: colors.grey[500], justifyContent: "flex-start" }} fullWidth>
                  ⚙️ Paramètres & Clé Google Maps
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
